import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { DynamicFormRenderer, createTempAttachmentUploadAdapter } from '@scientist/dynamic-form-renderer';
import { SimpleWysiwygRichText } from '@scientist/dynamic-form-renderer/richtext-simple';
import { DownshiftMultiSelect } from '@scientist/dynamic-form-renderer/enhanced-select';
import ChemDoodleWidget from '../widgets/ChemDoodleWidget.jsx';
import FieldTree from './FieldTree.jsx';
import FieldEditor from './FieldEditor.jsx';
import AceJsonEditor from './AceJsonEditor.jsx';
import JsonHelpPanel from './JsonHelpPanel.jsx';
import RendererEditor from './RendererEditor.jsx';
import styles from './DynamicFormBuilder.module.css';

const WIDGET_COMPONENTS = { chem_doodle: ChemDoodleWidget };

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function fieldAtPath(definition, path) {
  if (!path || path.length === 0) return null;
  let list = Array.isArray(definition?.fields) ? definition.fields : [];
  let field = null;
  for (const idx of path) {
    field = list[idx];
    if (!field) return null;
    list = Array.isArray(field.children) ? field.children : [];
  }
  return field;
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function keyPathFor(definition, path) {
  if (!path || path.length === 0) return '';
  const parts = [];
  let list = Array.isArray(definition?.fields) ? definition.fields : [];
  for (const idx of path) {
    const field = list[idx];
    if (!field) break;
    if (field.key) parts.push(field.key);
    list = Array.isArray(field.children) ? field.children : [];
  }
  return parts.join('.');
}

function errorsForKeyPath(errors, keyPath) {
  if (!Array.isArray(errors) || errors.length === 0 || !keyPath) return [];
  const prefix = `${keyPath}:`;
  return errors.filter((msg) => typeof msg === 'string' && msg.startsWith(prefix));
}

// Returns a new definition with the children array at `parentPath` replaced
// by `transform(array)`. parentPath = [] mutates the root `fields` array.
function withFieldsAt(definition, parentPath, transform) {
  const next = deepClone(definition);
  if (!Array.isArray(next.fields)) next.fields = [];

  if (parentPath.length === 0) {
    next.fields = transform(next.fields);
    return next;
  }

  let parent = next.fields[parentPath[0]];
  for (let i = 1; i < parentPath.length; i++) {
    if (!Array.isArray(parent.children)) parent.children = [];
    parent = parent.children[parentPath[i]];
  }
  if (!Array.isArray(parent.children)) parent.children = [];
  parent.children = transform(parent.children);
  return next;
}

function defaultFieldFor(siblings) {
  const existingKeys = new Set((siblings || []).map((f) => f?.key).filter(Boolean));
  let n = 1;
  while (existingKeys.has(`new_field_${n}`)) n++;
  return { key: `new_field_${n}`, type: 'text', label: '' };
}

export default function DynamicFormBuilder({
  definition: initialDefinition,
  typeCatalog,
  fieldReference,
  errors = [],
  warnings = [],
  hasDraft = false,
  hiddenInputName,
  makeActiveAction,
  saveDraftAction,
  discardDraftAction,
  renderers,
  useRendererInPdfs,
  sampleData,
  renderersAction,
  renderPreviewAction,
  rendererSaveFailed = false,
}) {
  const [definition, setDefinition] = useState(initialDefinition);
  const [selectedPath, setSelectedPath] = useState(null);
  // 'builder' shows the visual field tree + detail panes; 'json' replaces them
  // with a raw editable JSON view; 'renderers' replaces them with the renderer
  // template editors + previews. jsonDraft holds the editor's current text and
  // jsonError holds a parse error (null when the draft is valid JSON).
  const [mode, setMode] = useState(rendererSaveFailed ? 'renderers' : 'builder');
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [showJsonHelp, setShowJsonHelp] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  // The renderer is uncontrolled — it seeds once on mount and ignores later
  // `definition` prop changes. Bumping this nonce keys a fresh mount so the
  // preview re-snapshots the current definition; between bumps the preview keeps
  // any values the admin typed into it, isolated from builder editing.
  const [previewNonce, setPreviewNonce] = useState(0);
  // Live output payload the preview form would submit, keyed by field key.
  const previewRef = useRef(null);
  const [previewValues, setPreviewValues] = useState({});

  const togglePreview = useCallback(() => {
    setShowPreview((shown) => {
      // Opening (or re-opening) snapshots the latest definition.
      if (!shown) setPreviewNonce((n) => n + 1);
      return !shown;
    });
  }, []);
  const refreshPreview = useCallback(() => setPreviewNonce((n) => n + 1), []);

  // The renderer's onChange only fires on edits, so seed the output payload with
  // its default values whenever the preview (re)mounts — opening or Refresh both
  // bump previewNonce, remounting the renderer.
  useEffect(() => {
    if (showPreview && previewRef.current) {
      setPreviewValues(previewRef.current.getValue());
    }
  }, [showPreview, previewNonce]);

  const copyPreviewValues = useCallback(() => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(JSON.stringify(previewValues, null, 2));
    }
  }, [previewValues]);

  // Lets admins exercise upload fields in the preview. Files land in
  // Pg::TempAttachment, which is purged after 24 hours.
  const uploadAdapter = useMemo(
    () => createTempAttachmentUploadAdapter({ csrfToken: csrfToken() }),
    [],
  );

  const serialized = useMemo(() => JSON.stringify(definition), [definition]);
  const prettyJson = useMemo(() => JSON.stringify(definition, null, 2), [definition]);

  const copyJson = useCallback(() => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(prettyJson);
    }
  }, [prettyJson]);

  // Live-commit JSON edits: valid JSON becomes the new definition (and flows into
  // the hidden input on Save); invalid JSON only records the error and leaves the
  // last valid definition untouched.
  const onJsonChange = useCallback((text) => {
    setJsonDraft(text);
    try {
      const parsed = JSON.parse(text);
      setDefinition(parsed);
      setJsonError(null);
      setSelectedPath(null);
    } catch (err) {
      setJsonError(err.message);
    }
  }, []);

  const enterJsonMode = useCallback(() => {
    setJsonDraft(prettyJson);
    setJsonError(null);
    setMode('json');
  }, [prettyJson]);

  const enterBuilderMode = useCallback(() => {
    // Blocked while the draft is unparseable so edits aren't silently discarded.
    if (jsonError) return;
    setMode('builder');
  }, [jsonError]);
  const enterRenderersMode = useCallback(() => setMode('renderers'), []);
  const confirmDiscard = useCallback((e) => {
    if (!window.confirm('Clear this draft and revert to the active version? This cannot be undone.')) {
      e.preventDefault();
    }
  }, []);
  const selectedField = useMemo(() => fieldAtPath(definition, selectedPath), [definition, selectedPath]);
  const selectedKeyPath = useMemo(() => keyPathFor(definition, selectedPath), [definition, selectedPath]);
  const selectedFieldErrors = useMemo(() => errorsForKeyPath(errors, selectedKeyPath), [errors, selectedKeyPath]);

  const addAfter = useCallback((path) => {
    const parentPath = path.slice(0, -1);
    const idx = path[path.length - 1];
    setDefinition((prev) => {
      let newPath = null;
      const next = withFieldsAt(prev, parentPath, (siblings) => {
        const updated = [...siblings];
        updated.splice(idx + 1, 0, defaultFieldFor(siblings));
        newPath = [...parentPath, idx + 1];
        return updated;
      });
      setSelectedPath(newPath);
      return next;
    });
  }, []);

  const addChild = useCallback((path) => {
    setDefinition((prev) => {
      let newPath = null;
      const next = withFieldsAt(prev, path, (children) => {
        const updated = [...children, defaultFieldFor(children)];
        newPath = [...path, updated.length - 1];
        return updated;
      });
      setSelectedPath(newPath);
      return next;
    });
  }, []);

  const removeAt = useCallback((path) => {
    const parentPath = path.slice(0, -1);
    const idx = path[path.length - 1];
    setDefinition((prev) => withFieldsAt(prev, parentPath, (siblings) => {
      const updated = [...siblings];
      updated.splice(idx, 1);
      return updated;
    }));
    setSelectedPath(null);
  }, []);

  const moveBy = useCallback((path, delta) => {
    const parentPath = path.slice(0, -1);
    const idx = path[path.length - 1];
    setDefinition((prev) => withFieldsAt(prev, parentPath, (siblings) => {
      const target = idx + delta;
      if (target < 0 || target >= siblings.length) return siblings;
      const updated = [...siblings];
      [updated[idx], updated[target]] = [updated[target], updated[idx]];
      return updated;
    }));
    setSelectedPath(null);
  }, []);

  const updateFieldAt = useCallback((path, transform) => {
    if (!path || path.length === 0) return;
    const parentPath = path.slice(0, -1);
    const idx = path[path.length - 1];
    setDefinition((prev) => withFieldsAt(prev, parentPath, (siblings) => {
      const updated = [...siblings];
      updated[idx] = transform(updated[idx]);
      return updated;
    }));
  }, []);

  const addRootField = useCallback(() => {
    setDefinition((prev) => {
      let newPath = null;
      const next = withFieldsAt(prev, [], (siblings) => {
        const updated = [...siblings, defaultFieldFor(siblings)];
        newPath = [updated.length - 1];
        return updated;
      });
      setSelectedPath(newPath);
      return next;
    });
  }, []);

  return (
    <div className="d-flex flex-column gap-3 p-3 border rounded bg-light">
      {/* The mode toggle lives outside the definition <form> because the
          Renderers mode has its own <form> (HTML forms can't nest) and saves to
          a separate endpoint. */}
      <div className="btn-group btn-group-sm" role="group" aria-label="Editor mode">
        <button
          type="button"
          className={`btn ${mode === 'builder' ? 'btn-secondary' : 'btn-outline-secondary'}`}
          aria-pressed={mode === 'builder'}
          disabled={mode === 'json' && !!jsonError}
          onClick={enterBuilderMode}
        >
          Builder
        </button>
        <button
          type="button"
          className={`btn ${mode === 'json' ? 'btn-secondary' : 'btn-outline-secondary'}`}
          aria-pressed={mode === 'json'}
          onClick={enterJsonMode}
        >
          Edit as JSON
        </button>
        <button
          type="button"
          className={`btn ${mode === 'renderers' ? 'btn-secondary' : 'btn-outline-secondary'}`}
          aria-pressed={mode === 'renderers'}
          onClick={enterRenderersMode}
        >
          Renderers
        </button>
      </div>

      {hasDraft && mode !== 'renderers' && (
        <div className="alert alert-info mb-0" role="status">
          You are editing an unsaved <strong>draft</strong> — it is not yet the active version.
        </div>
      )}

      {warnings.length > 0 && mode !== 'renderers' && (
        <div className="alert alert-warning mb-0" role="alert">
          <strong>This draft has validation issues — fix them before making it active:</strong>
          <ul className="mt-1 ps-4 mb-0">
            {warnings.map((message, i) => (
              <li key={i}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {errors.length > 0 && (
        <div className="alert alert-danger mb-0" role="alert">
          <strong>Save failed — fix the following and try again:</strong>
          <ul className="mt-1 ps-4 mb-0">
            {errors.map((message, i) => (
              <li key={i}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      {mode === 'renderers' ? (
        <RendererEditor
          renderers={renderers}
          useRendererInPdfs={useRendererInPdfs}
          sampleData={sampleData}
          previewValues={previewValues}
          renderersAction={renderersAction}
          renderPreviewAction={renderPreviewAction}
        />
      ) : (
      // The editing panes only mutate React state; the serialized definition
      // flows to the server via the hidden inputs in the footer's save forms
      // (which is why the panes live outside any <form>).
      <div className="d-flex flex-column gap-3">

      {mode === 'builder' ? (
      <div className={styles.panes}>
        <section className={`p-3 bg-white border rounded ${styles.paneMinHeight}`} aria-label="Field tree">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h3 className="m-0 text-uppercase text-muted small">Fields</h3>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRootField}>
              + Add field
            </button>
          </div>
          <FieldTree
            fields={Array.isArray(definition?.fields) ? definition.fields : []}
            selectedPath={selectedPath}
            errors={errors}
            onSelect={setSelectedPath}
            onAddAfter={addAfter}
            onAddChild={addChild}
            onRemove={removeAt}
            onMoveUp={(p) => moveBy(p, -1)}
            onMoveDown={(p) => moveBy(p, 1)}
          />
        </section>

        <section className={`p-3 bg-white border rounded ${styles.paneMinHeight}`} aria-label="Field detail">
          <h3 className="m-0 text-uppercase text-muted small mb-2">Detail</h3>
          <FieldEditor
            field={selectedField}
            path={selectedPath}
            definition={definition}
            typeCatalog={typeCatalog}
            fieldErrors={selectedFieldErrors}
            onChange={updateFieldAt}
          />
        </section>
      </div>
      ) : (
      <section className="d-flex flex-column gap-2 p-3 bg-white border rounded" aria-label="JSON editor">
        <div className="d-flex justify-content-between align-items-baseline gap-2">
          <h3 className="m-0 text-uppercase text-muted small">JSON</h3>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowJsonHelp((v) => !v)}>
              {showJsonHelp ? 'Hide help' : 'Show help'}
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyJson}>
              Copy JSON
            </button>
          </div>
        </div>
        <div className={showJsonHelp ? styles.jsonLayout : undefined}>
          <div className="d-flex flex-column gap-2">
            <AceJsonEditor value={jsonDraft} onChange={onJsonChange} ariaLabel="Form definition JSON" />
            {jsonError && (
              <div className="alert alert-warning mb-0 py-1 px-2 small" role="alert">
                Invalid JSON — {jsonError}
              </div>
            )}
          </div>
          {showJsonHelp && <JsonHelpPanel typeCatalog={typeCatalog} fieldReference={fieldReference} />}
        </div>
      </section>
      )}

      {/* Three independent save forms: forms can't nest and these actions use
          different HTTP verbs, so each is self-contained and carries its own
          serialized definition. */}
      <div className="d-flex justify-content-end gap-2">
        {hasDraft && (
          <form method="post" action={discardDraftAction} onSubmit={confirmDiscard}>
            <input type="hidden" name="_method" value="delete" />
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <button type="submit" className="btn btn-outline-danger">Clear and revert to active</button>
          </form>
        )}
        <form method="post" action={saveDraftAction}>
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <input type="hidden" name={hiddenInputName} value={serialized} />
          <button type="submit" className="btn btn-primary" disabled={mode === 'json' && !!jsonError}>Save draft</button>
        </form>
        <form method="post" action={makeActiveAction}>
          <input type="hidden" name="_method" value="patch" />
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <input type="hidden" name={hiddenInputName} value={serialized} />
          <button type="submit" className="btn btn-success" disabled={mode === 'json' && !!jsonError}>Save and make active</button>
        </form>
      </div>
      </div>
      )}

      {/* Preview lives OUTSIDE the builder <form> so its controls never submit
          the builder or participate in its values. The renderer is uncontrolled
          and validation here is purely visual — it never blocks Save. */}
      <section className="d-flex flex-column gap-2 p-3 bg-white border rounded" aria-label="Preview">
        <div className="d-flex justify-content-between align-items-baseline gap-2">
          <h3 className="m-0 text-uppercase text-muted small">Preview</h3>
          <div className="d-flex gap-2">
            {showPreview && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={refreshPreview}>
                Refresh preview
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={togglePreview}>
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
          </div>
        </div>
        {showPreview && (
          <div className="border rounded p-3">
            <DynamicFormRenderer
              key={previewNonce}
              ref={previewRef}
              definition={definition}
              mode="preview"
              validateOnMount
              onChange={() => setPreviewValues(previewRef.current.getValue())}
              richTextAdapter={SimpleWysiwygRichText}
              enhancedSelectAdapter={DownshiftMultiSelect}
              widgetComponents={WIDGET_COMPONENTS}
              uploadAdapter={uploadAdapter}
            />
          </div>
        )}
        {showPreview && (
          <div className="d-flex flex-column gap-1" aria-label="Output data">
            <div className="d-flex justify-content-between align-items-baseline gap-2">
              <h4 className="m-0 text-uppercase text-muted small">Output data</h4>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyPreviewValues}>
                Copy
              </button>
            </div>
            <pre className={`bg-light border rounded p-3 small mb-0 font-monospace ${styles.json}`}>{JSON.stringify(previewValues, null, 2)}</pre>
          </div>
        )}
      </section>
    </div>
  );
}
